<script>
import { mapState, mapActions } from "vuex";
import ContractNavigationWrapper from "@/components/ContractNavigationWrapper";
import MultiSelect from "@/components/MultiSelect";

export default {
  // Included components
  components: {
    ContractNavigationWrapper,
    MultiSelect,
  },

  data() {
    return {
      // The ID of the contract being edited, or null if creating
      contractId: null,
      contract: null,
      team: null,
      teamId: null,
      facility: null,
      facilityId: null,
      batch_number: null,
      rate_per_kWh: null,
      machine_setup_fee: null,
      machine_security_deposit: null,
      machine_insurance: null,
      term_length: null,
      machine_count: null,
      customer_type: null,
      device_type_id: null,
      compatible: null,
      hourly_watt_consumption: null,
      shipped_or_transferred: null,
      invoiced_in_stripe: null,
      start_date: null,
      expiration: null,
      allTeamsForContracts: [],
      teamWorkers: {},

      // Workers selected to be in the Contract
      selectedWorkers: [],

      // whether data is currently being fetched
      loading: true,

      placeholder: null,
    };
  },

  computed: {
    // Whether the form is being used to edit (vs. create)
    editing() {
      return this.contractId != null;
    },

    // The workerIds of the selected workers
    selectedWorkerIds() {
      return this.selectedWorkers.map((worker) => worker.id);
    },

    // Options for the device selector
    deviceOptions() {
      return this.deviceTypes.map((type) => {
        return { value: type.id, text: type.name };
      });
    },

    // Options for the device selector
    teamOptions() {
      return this.allTeamsForContracts.map((type) => {
        return { value: type.id, text: type.handle };
      });
    },

    // Options for the device selector
    facilityOptions() {
      return this.contractFacilities.map((type) => {
        return { value: type.id, text: type.name };
      });
    },

    originOptions() {
      return [
        { value: "shipped", text: "Shipped" },
        { value: "transferred", text: "Transferred" },
      ];
    },

    invoicedInStripeOptions() {
      return [
        { value: true, text: "Yes" },
        { value: false, text: "No" },
      ];
    },

    customerTypeOptions() {
      return [
        { value: "Navier", text: "Navier" },
        { value: "Blockware", text: "Blockware" },
      ];
    },

    compatibilityOptions() {
      return [
        { value: true, text: "Yes" },
        { value: false, text: "No" },
      ];
    },

    // Mapped vuex state
    ...mapState(["contracts", "deviceTypes", "user"]),
  },

  // When the component is created, fetch the worker (if in edit mode)
  // and get the pool and device options
  async created() {
    if (!this.user.isStaffAdmin) {
      await this.$router.push("/");
    }

    this.loading = true;
    this.contractId = this.$route.params.contractId;

    if (this.editing) {
      this.contract = await this.getContract({ contractId: this.contractId });
      // Set form fields
      this.team = this.contract.team;
      this.teamId = this.team.id;
      this.facility = this.contract.facility;
      this.facilityId = this.facility.id;
      this.batch_number = this.contract.batch_number;
      this.rate_per_kWh = this.contract.rate_per_kWh;
      this.machine_setup_fee = this.contract.machine_setup_fee;
      this.machine_security_deposit = this.contract.machine_security_deposit;
      this.machine_insurance = this.contract.machine_insurance;
      this.term_length = this.contract.term_length;
      this.machine_count = this.contract.machine_count;
      this.customer_type = this.contract.customer_type;
      this.device_type_id = this.contract.device_type.id;
      this.compatible = this.contract.compatible;
      this.hourly_watt_consumption = this.contract.hourly_watt_consumption;
      this.shipped_or_transferred = this.contract.shipped_or_transferred;
      this.invoiced_in_stripe = this.contract.invoiced_in_stripe;
      this.start_date = this.contract.start_date;
      this.expiration = this.contract.expiration;
      this.teamWorkers = await this.getWorkersForTeam({
        contractId: this.contractId,
        teamId: this.teamId,
      });

      this.selectedWorkers = Object.values(this.teamWorkers).filter(
        (worker) => this.contract.worker_ids.indexOf(worker.id) !== -1
      );
    }

    this.allTeamsForContracts = await this.getAllTeamsForContracts();
    this.contractFacilities = await this.getContractFacilities();
    await this.getDeviceTypes();
    this.loading = false;
  },

  methods: {
    // On submit, either put or post the form data to the API,
    // depending on whether in edit or create mode
    async submit() {
      if (this.editing) {
        this.put();
      } else {
        this.post();
      }
    },

    // Update the worker via the API
    async put() {
      // Update given the form inputs
      const success = await this.putContract({
        contractId: this.contractId,
        teamId: this.teamId,
        facilityId: this.facilityId,
        batch_number: this.batch_number,
        rate_per_kWh: this.rate_per_kWh,
        machine_setup_fee: this.machine_setup_fee,
        machine_security_deposit: this.machine_security_deposit,
        machine_insurance: this.machine_insurance,
        term_length: this.term_length,
        machine_count: this.machine_count,
        customer_type: this.customer_type,
        device_type_id: this.device_type_id,
        compatible: this.compatible,
        hourly_watt_consumption: this.hourly_watt_consumption,
        shipped_or_transferred: this.shipped_or_transferred,
        selectedWorkerIds: this.selectedWorkerIds,
        invoiced_in_stripe: this.invoiced_in_stripe,
        start_date: this.start_date,
        expiration: this.expiration,
      });

      // If success, then redirect
      if (success) {
        await this.$router.push("/contracts/" + this.contractId);
      }
    },

    // Post the new worker via the API
    async post() {
      // Update given the form inputs
      const success = await this.postContract({
        teamId: this.teamId,
        facilityId: this.facilityId,
        batch_number: this.batch_number,
        rate_per_kWh: this.rate_per_kWh,
        machine_setup_fee: this.machine_setup_fee,
        machine_security_deposit: this.machine_security_deposit,
        machine_insurance: this.machine_insurance,
        term_length: this.term_length,
        machine_count: this.machine_count,
        customer_type: this.customer_type,
        device_type_id: this.device_type_id,
        compatible: this.compatible,
        hourly_watt_consumption: this.hourly_watt_consumption,
        shipped_or_transferred: this.shipped_or_transferred,
        selectedWorkerIds: this.selectedWorkerIds,
        invoiced_in_stripe: this.invoiced_in_stripe,
        start_date: this.start_date,
        expiration: this.expiration,
      });

      // If success, then redirect
      if (success) {
        await this.$router.push("/contracts");
      }
    },

    async getTeamWorkers() {
      this.teamWorkers = await this.getWorkersForTeam({
        contractId: this.contractId,
        teamId: this.teamId,
      });
      if (this.editing) {
        this.selectedWorkers = Object.values(this.teamWorkers).filter(
          (worker) => this.contract.worker_ids.indexOf(worker.id) !== -1
        );
      }
    },

    // Return whether a given worker matches a given search string
    workerMatchesSearch(search, worker) {
      search = search.trim().toLowerCase();
      return worker.name.toLowerCase().includes(search);
    },

    // Mapped vuex actions
    ...mapActions([
      "getContract",
      "putContract",
      "postContract",
      "getDeviceTypes",
      "getPools",
      "getAllTeamsForContracts",
      "getContractFacilities",
      "getWorkersForTeam",
    ]),
  },
};
</script>

<template>
  <ContractNavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/contracts">Contracts</b-breadcrumb-item>
      <b-breadcrumb-item v-if="editing" :to="`/contracts/${contractId}`">
        {{
          team != null
            ? team.handle + "." + facility.name + "." + contract.batch_number
            : "Contract"
        }}
      </b-breadcrumb-item>
      <b-breadcrumb-item v-if="editing" active>Edit</b-breadcrumb-item>
      <b-breadcrumb-item v-else active>Add Contract</b-breadcrumb-item>
    </template>
    <h1 class="mb-3">{{ editing ? "Edit" : "Add" }} Contract</h1>
    <div v-if="!loading">
      <b-row>
        <b-col>
          <b-card class="mb-3">
            <b-form @submit.stop.prevent="submit">
              <b-form-group id="Team" label="Team" label-for="Team">
                <b-form-select
                  required
                  v-model="teamId"
                  id="Team"
                  @input="getTeamWorkers"
                  :options="teamOptions"
                >
                  <template #first>
                    <option :value="null" disabled>Select a Team</option>
                  </template>
                </b-form-select>
              </b-form-group>

              <b-form-group id="facility" label="Facility" label-for="facility">
                <b-form-select
                  required
                  v-model="facilityId"
                  id="facilityId"
                  :options="facilityOptions"
                >
                  <template #first>
                    <option :value="null" disabled>Select a Facility</option>
                  </template>
                </b-form-select>
              </b-form-group>
              <b-form-group
                id="customer-type"
                label="Customer Type"
                label-for="customerType"
              >
                <b-form-select
                  required
                  v-model="customer_type"
                  id="customerType"
                  :options="customerTypeOptions"
                >
                  <template #first>
                    <option :value="null" disabled>Navier or Blockware</option>
                  </template>
                </b-form-select>
                <small class="form-text text-muted">
                  Is the customer from Navier or Blockware
                </small>
              </b-form-group>

              <hr />
              <h4>Input Hosting And Machine Terms</h4>
              <br />

              <b-row>
                <b-col>
                  <b-form-group
                    id="batch-number"
                    label="Batch Number"
                    label-for="batchNumber"
                  >
                    <b-form-input
                      required
                      id="batchNumber"
                      v-model="batch_number"
                      placeholder="0"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">Numbers Only</small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="device-type-group"
                    label="Machine Model"
                    label-for="deviceTypes"
                  >
                    <b-form-select
                      required
                      v-model="device_type_id"
                      id="deviceTypes"
                      :options="deviceOptions"
                    >
                      <template #first>
                        <option :value="null" disabled></option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      Type of machines on this contract
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="compatible"
                    label="Machine Compatibility with Mine.app"
                    label-for="compatible"
                  >
                    <b-form-select
                      required
                      v-model="compatible"
                      id="compatible"
                      :options="compatibilityOptions"
                    >
                      <template #first>
                        <option :value="null" disabled>Yes or No</option>
                      </template>
                    </b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col v-if="!editing">
                  <b-form-group
                    id="machine-count"
                    label="Quantity of Machines"
                    label-for="machineCount"
                  >
                    <b-form-input
                      required
                      id="machineCount"
                      v-model="machine_count"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">Numbers only</small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="rate-per-kWh"
                    label="Rate/kWh"
                    label-for="ratePerKWh"
                  >
                    <b-form-input
                      required
                      id="ratePerKWh"
                      v-model="rate_per_kWh"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">Numbers only</small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="hourly-watt-consumption"
                    label="Hourly Watt Consumption"
                    label-for="hourlyWattConsumption"
                  >
                    <b-form-input
                      required
                      id="hourlyWattConsumption"
                      v-model="hourly_watt_consumption"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">Numbers only</small>
                  </b-form-group>
                </b-col>
              </b-row>

              <div>
                <b-form-group
                  v-if="teamId != null"
                  id="workers-group"
                  label="Workers"
                  label-for="workers"
                >
                  <MultiSelect
                    v-model="selectedWorkers"
                    :options="teamWorkers"
                    :match="workerMatchesSearch"
                  ></MultiSelect>
                </b-form-group>

                <b-form-group
                  v-else
                  id="workers-group"
                  label="Workers"
                  label-for="workers"
                >
                  <b-form-select required id="workers" :options="[]">
                    <template #first>
                      <option :value="null" disabled>
                        Please Select The Team Before Selecting Workers
                      </option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </div>

              <b-row>
                <b-col>
                  <b-form-group
                    id="term-length"
                    label="Term Length (Months)"
                    label-for="termLength"
                  >
                    <b-form-input
                      required
                      id="termLength"
                      v-model="term_length"
                      placeholder="0"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">Numbers only</small>
                  </b-form-group>
                </b-col>

                <b-col>
                  <b-form-group
                    id="startDateGroup"
                    label="Contract Start"
                    label-for="startDate"
                  >
                    <b-form-datepicker
                      id="startDate"
                      v-model="start_date"
                    ></b-form-datepicker>
                    <small class="form-text text-muted">
                      Contract Start Date
                    </small>
                  </b-form-group>
                </b-col>

                <b-col>
                  <b-form-group
                    id="expirationDateGroup"
                    label="Contract Expiry"
                    label-for="expirationDate"
                  >
                    <b-form-datepicker
                      id="expirationDate"
                      v-model="expiration"
                    ></b-form-datepicker>
                    <small class="form-text text-muted">
                      Contract Expiration Date
                    </small>
                  </b-form-group>
                </b-col>

                <b-col>
                  <b-form-group
                    id="machine-setup-fee"
                    label="Setup Fee"
                    label-for="machineSetupFee"
                  >
                    <b-form-input
                      required
                      id="machineSetupFee"
                      v-model="machine_setup_fee"
                      placeholder="0"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Per Unit; Numbers only
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="machine-security-deposit"
                    label="Security Deposit"
                    label-for="machineSecurityDeposit"
                  >
                    <b-form-input
                      required
                      id="machineSecurityDeposit"
                      v-model="machine_security_deposit"
                      placeholder="0.0"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Per Unit; Numbers only
                    </small>
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group
                    id="machine-insurance"
                    label="Insurance"
                    label-for="machineInsurance"
                  >
                    <b-form-input
                      required
                      id="machineInsurance"
                      v-model="machine_insurance"
                      autocomplete="off"
                    ></b-form-input>
                    <small class="form-text text-muted">
                      Per Unit; Numbers only
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-form-group
                    id="shipped_or_transferred"
                    label="Miner Origin"
                    label-for="shippedOrTransferred"
                  >
                    <b-form-select
                      required
                      v-model="shipped_or_transferred"
                      id="shippedOrTransferred"
                      :options="originOptions"
                    >
                      <template #first>
                        <option :value="null" disabled>
                          Shipped Or Transferred
                        </option>
                      </template>
                    </b-form-select>
                    <small class="form-text text-muted">
                      Were the machines shipped or transferred
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-form-group
                    id="invoiced_in_stripe"
                    label="Will this contract be invoiced in stripe?"
                    label-for="invoicedInStripe"
                  >
                    <b-form-select
                      required
                      v-model="invoiced_in_stripe"
                      id="invoicedInStripe"
                      :options="invoicedInStripeOptions"
                    >
                      <template #first>
                        <option :value="null" disabled>
                          Invoiced via Stripe
                        </option>
                      </template>
                    </b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-button type="submit" variant="primary">
                {{ editing ? "Update" : "Add" }} Contract
              </b-button>
            </b-form>
          </b-card>
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <b-row>
        <b-col class="text-center mt-5">
          <div class="mb-4">
            <b-spinner
              style="width: 4rem; height: 4rem"
              variant="primary"
              label="Loading Contract..."
            ></b-spinner>
          </div>
          <b>Loading Contract...</b>
        </b-col>
      </b-row>
    </div>
  </ContractNavigationWrapper>
</template>

<style>
#toggle-btn {
  padding: 7px 12px;
  margin-top: -1px;
}
</style>
